import _ from 'lodash';
import { RISK_PRESENTATION } from './riskPresentation';
import { displayPct } from '@/modules/DataAnalysis/Risk/riskFormat';

// "0 degC" / "30 degC" -> Fahrenheit integer when unitCode is 'F'.
// "0.5" (dimensionless, e.g. arid) -> parsed as a plain number.
export function parseCatalogThreshold(defaultStr, unitCode) {
    if (defaultStr == null) return null;
    const match = /^(-?[\d.]+)\s*deg(C|F)$/i.exec(String(defaultStr).trim());
    if (match) {
        const value = parseFloat(match[1]);
        const isCelsius = match[2].toUpperCase() === 'C';
        if (unitCode === 'F') return Math.round(isCelsius ? (value * 9) / 5 + 32 : value);
        if (unitCode === 'C') return Math.round(isCelsius ? value : ((value - 32) * 5) / 9);
        return value;
    }
    const num = parseFloat(defaultStr);
    return Number.isNaN(num) ? null : num;
}

// Live GET /risk returns { code, data: [{ id, provider, indicator, threshold,
// window, screen_offset }] } — no label/available/presets/chart_title/unit.
// Merge each entry with its RISK_PRESENTATION copy; drop any catalog id with
// no presentation entry rather than rendering a chip with no copy.
export function normalizeCatalog(payload) {
    const entries = (payload && payload.data) || [];
    const merged = [];

    entries.forEach((entry) => {
        const presentation = RISK_PRESENTATION[entry.id];
        if (!presentation) {
            // eslint-disable-next-line no-console
            console.warn(`[risk] catalog id "${entry.id}" has no presentation entry — hiding its chip.`);
            return;
        }
        // The catalog's own default and the design's default disagree only for
        // freeze (see riskPresentation.js) — presentation wins when set. arid and
        // chill_hours both mirror the live catalog's own default rather than
        // overriding it, but still state defaultThreshold explicitly (see each
        // entry's own comment for why leaving it undefined is unsafe).
        const defaultThreshold = presentation.defaultThreshold != null
            ? presentation.defaultThreshold
            : parseCatalogThreshold(entry.threshold && entry.threshold.default, presentation.unitCode);

        merged.push({
            id: entry.id,
            provider: entry.provider,
            indicator: entry.indicator,
            window: entry.window,
            screenOffset: entry.screen_offset,
            catalogThreshold: entry.threshold,
            ...presentation,
            available: true,
            defaultThreshold,
        });
    });

    return merged.sort((a, b) => a.order - b.order);
}

// Used when GET /risk fails — the tab must still work with degraded (but
// fully labeled) chips rather than breaking entirely.
export function synthesizeCatalogFromPresentation() {
    return Object.keys(RISK_PRESENTATION)
        .map((id) => {
            const presentation = RISK_PRESENTATION[id];
            return {
                id,
                provider: null,
                indicator: null,
                window: null,
                screenOffset: false,
                catalogThreshold: null,
                ...presentation,
                available: true,
                defaultThreshold: presentation.defaultThreshold != null ? presentation.defaultThreshold : null,
            };
        })
        .sort((a, b) => a.order - b.order);
}

// Normalizes a probability response (designed contract) into the module's
// internal shape. Mostly pass-through, but does the derived work once so
// every block reads pre-computed numbers instead of re-deriving them:
//   - pctRaw / pctDisplay attached to `period` and every bucket
//   - buckets sorted chronologically
//   - period.peak_weeks (bucket start dates) resolved into full bucket refs
//   - warnings surfaced as { smallSample, noData }
//
// probability: null (the no-data fixture) is preserved as null throughout —
// NOT coerced to 0 — because Math.round(null * 100) is 0, which would render
// a confident "0%" on a screen that has no data at all.
export function normalizeProbability(payload) {
    const location = payload.location || {};
    const provenance = payload.provenance || {};
    const warnings = payload.warnings || [];
    const period = payload.period || {};

    const smallSampleWarning = warnings.find((w) => w.code === 'small_sample') || null;
    const noDataWarning = warnings.find((w) => w.code === 'no_data') || null;

    const smallSample = {
        active: !!smallSampleWarning,
        message: smallSampleWarning ? smallSampleWarning.message : null,
        precision: smallSampleWarning ? smallSampleWarning.precision || 10 : 1,
        // The sample the numbers on screen were actually computed from, NOT the nominal
        // width of the climatology window. The warning fires precisely because
        // period.years_total fell under the minimum, so reporting
        // provenance.climatology_window.n_years here made the provenance line announce
        // "n = 45 years" in the same breath as "your sample is too small".
        nYears: period.years_total != null ? period.years_total : _.get(provenance, 'climatology_window.n_years', null),
        windowLabel: provenance.climatology_window
            ? `${provenance.climatology_window.start_year}–${provenance.climatology_window.end_year}`
            : null,
        // Server-declared per-indicator minimum (30 for the 1981+ indicators, 7 for
        // chill_hours, whose entire record IS 7 years — see risk/spec.py#small_sample_min).
        // Falls back to 30 only for a payload that predates this field.
        minimum: (smallSampleWarning && smallSampleWarning.minimum) || 30,
    };

    const buckets = [...(payload.buckets || [])]
        .sort((a, b) => (a.start_date < b.start_date ? -1 : a.start_date > b.start_date ? 1 : 0))
        .map((bucket) => {
            const pctRaw = bucket.probability == null ? null : bucket.probability * 100;
            return {
                ...bucket,
                pctRaw,
                pctDisplay: displayPct(pctRaw, smallSample),
            };
        });

    const bucketsByStart = new Map(buckets.map((b) => [b.start_date, b]));
    const periodPctRaw = period.probability == null ? null : period.probability * 100;
    const peakWeeks = (period.peak_weeks || [])
        .map((startDate) => bucketsByStart.get(startDate))
        .filter(Boolean)
        .map((b) => ({ label: b.label, labelShort: b.label_short, pctDisplay: b.pctDisplay }));

    return {
        indicator: payload.indicator,
        units: payload.units,
        threshold: payload.threshold,
        op: payload.op,
        // Echoed by the heat index indicators; the summary states the condition with them.
        category: payload.category != null ? payload.category : null,
        min_hours: payload.min_hours != null ? payload.min_hours : null,
        aggregation: payload.aggregation,
        location,
        period: {
            ...period,
            pctRaw: periodPctRaw,
            pctDisplay: displayPct(periodPctRaw, smallSample),
            peakWeeks,
        },
        buckets,
        provenance,
        warnings: {
            smallSample,
            // NOT buckets.length === 0: a period-only indicator (chill_hours,
            // weekly: false in riskPresentation.js) legitimately has zero buckets on a
            // perfectly good result — Risk.html hides the chart/table for it, it never
            // means "no data". years_total === 0 is the API's own signal for that
            // (climatology.py sets warnings=[no_data] and buckets=[] together whenever
            // it fires), so read the real signal instead of a coincidental one.
            noData: !!noDataWarning || (payload.period || {}).years_total === 0,
        },
    };
}
